import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  InventoryPackage, 
  InventoryPackageInsert, 
  Customer as ModelCustomer,
  Event as ModelEvent,
  Payment as ModelPayment,
  Currency,
  Language
} from '@/types/models';
import { db, auth } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, getCountFromServer, serverTimestamp } from 'firebase/firestore';

// Types mapping previous Supabase definitions and current user requirements
export type Customer = ModelCustomer;

export type Contact = {
    id: string;
    customer_id: string;
    user_id: string;
    name: string;
    role?: string | null;
    phone?: string | null;
    email?: string | null;
    created_at: string;
    updated_at: string;
};

export type AppEvent = ModelEvent & { customers?: Record<string, unknown> };

export type Payment = ModelPayment & { events?: Record<string, unknown>; userId?: string };

export type Proposal = { 
    id: string; 
    user_id: string | null; 
    name: string; 
    email: string; 
    phone?: string | null;
    event_type?: string | null;
    date?: string | null;
    status?: string | null;
    message: string | null; 
    created_at: string 
};

export type UserBillingConfig = {
    userId: string;
    taxIdType: '01' | '02' | '03' | '04'; // Física, Jurídica, DIMEX, NITE
    taxId: string;
    legalName: string;
    commercialName?: string;
    email: string;
    phone: string;
    country: string;
    province?: string;
    canton?: string;
    distrito?: string;
    state?: string;
    city?: string;
    zipCode?: string;
    barrio?: string;
    addressDetail: string;
    p12Url?: string; // Stored in Firebase Storage
    hasP12: boolean;
    updated_at: string;
};

type CustomerInsert = Omit<Customer, 'id' | 'created_at' | 'updated_at'>;
type EventInsert = Omit<AppEvent, 'id' | 'created_at' | 'updated_at' | 'customers'>;
type PaymentInsert = Omit<Payment, 'id' | 'created_at' | 'updated_at' | 'events'>;
type ProposalInsert = Omit<Proposal, 'id' | 'created_at' | 'user_id'>;

// Customers
export const useCustomers = () => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['customers', user?.uid],
        queryFn: async () => {
            if (!user) return [];
            const q = query(collection(db, 'customers'), where('user_id', '==', user.uid));
            const snapshot = await getDocs(q);
            const customers = snapshot.docs.map(doc => {
                const data = doc.data();
                return { 
                    id: doc.id, 
                    name: data.name,
                    email: data.email,
                    phone: data.phone,
                    notes: data.notes,
                    identificationNumber: data.identificationNumber,
                    type: data.type || 'fisica',
                    clientType: data.clientType,
                    legalName: data.legalName,
                    address: data.address,
                    userId: data.user_id,
                    createdAt: new Date(data.created_at || new Date().toISOString()),
                    updatedAt: new Date(data.updated_at || new Date().toISOString())
                } as Customer;
            });
            // Sort in memory to avoid needing a composite index
            return customers.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        },
    });
};

export const useCustomer = (id: string) => {
    return useQuery({
        queryKey: ['customers', id],
        queryFn: async () => {
            const docRef = doc(db, 'customers', id);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) throw new Error('Customer not found');
            const data = docSnap.data();
            return { 
                id: docSnap.id, 
                name: data.name,
                email: data.email,
                phone: data.phone,
                notes: data.notes,
                identificationNumber: data.identificationNumber,
                type: data.type || 'fisica',
                clientType: data.clientType,
                legalName: data.legalName,
                address: data.address,
                userId: data.user_id,
                createdAt: new Date(data.created_at),
                updatedAt: new Date(data.updated_at)
            } as Customer;
        },
        enabled: !!id,
    });
};

export const useUpsertCustomer = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async (customer: CustomerInsert & { id?: string }) => {
            if (!user) throw new Error('User not authenticated');

            const timestamp = new Date().toISOString();
            const customerData: Record<string, unknown> = { 
                name: customer.name,
                email: customer.email || null,
                phone: customer.phone || null,
                notes: customer.notes || null,
                identificationNumber: customer.identificationNumber || '',
                type: customer.type || 'fisica',
                clientType: customer.clientType || null,
                legalName: customer.legalName || null,
                address: customer.address || null,
                updated_at: timestamp
            };

            if (customer.id) {
                await updateDoc(doc(db, 'customers', customer.id), customerData);
                return { id: customer.id, ...customer };
            } else {
                customerData.user_id = user.uid;
                customerData.created_at = timestamp;
                const docRef = await addDoc(collection(db, 'customers'), customerData);
                return { id: docRef.id, ...customer };
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customers'] });
        },
    });
};

export const useDeleteCustomer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            await deleteDoc(doc(db, 'customers', id));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customers'] });
        },
    });
};

// Contacts (Linked to Customers/Companies)
export const useContacts = (customerId: string) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['contacts', customerId, user?.uid],
        queryFn: async () => {
            if (!user || !customerId) return [];
            const q = query(
                collection(db, 'contacts'), 
                where('customer_id', '==', customerId), 
                where('user_id', '==', user.uid)
            );
            const snapshot = await getDocs(q);
            const contacts = snapshot.docs.map(doc => {
                const data = doc.data();
                return { 
                    id: doc.id, 
                    customerId: data.customer_id,
                    name: data.name,
                    role: data.role,
                    email: data.email,
                    phone: data.phone,
                    userId: data.user_id,
                    createdAt: new Date(data.created_at || new Date().toISOString()),
                    updatedAt: new Date(data.updated_at || new Date().toISOString())
                } as Contact;
            });
            return contacts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        },
        enabled: !!customerId && !!user,
    });
};

export const useUpsertContact = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async (contact: Partial<Contact> & { customerId: string }) => {
            if (!user) throw new Error('User not authenticated');

            const timestamp = new Date().toISOString();
            const contactData: Record<string, unknown> = { 
                customer_id: contact.customerId,
                name: contact.name,
                role: contact.role || null,
                email: contact.email || null,
                phone: contact.phone || null,
                updated_at: timestamp
            };

            if (contact.id) {
                await updateDoc(doc(db, 'contacts', contact.id), contactData);
                return { id: contact.id, ...contact };
            } else {
                contactData.user_id = user.uid;
                contactData.created_at = timestamp;
                const docRef = await addDoc(collection(db, 'contacts'), contactData);
                return { id: docRef.id, ...contact };
            }
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['contacts', variables.customerId] });
        },
    });
};

export const useDeleteContact = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (vars: { id: string, customerId: string }) => {
            await deleteDoc(doc(db, 'contacts', vars.id));
            return vars.customerId;
        },
        onSuccess: (customerId) => {
            queryClient.invalidateQueries({ queryKey: ['contacts', customerId] });
        },
    });
};

// User Billing Configuration (per DJ/User)
export const useUserBillingConfig = () => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['userBillingConfig', user?.uid],
        queryFn: async () => {
            if (!user) return null;
            const docRef = doc(db, 'user_billing_configs', user.uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { userId: docSnap.id, ...docSnap.data() } as UserBillingConfig;
            }
            return null;
        },
        enabled: !!user,
    });
};

export const useUpsertUserBillingConfig = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async (config: Partial<UserBillingConfig>) => {
            if (!user) throw new Error('User not authenticated');
            const docRef = doc(db, 'user_billing_configs', user.uid);
            const sanitizedConfig = Object.fromEntries(
                Object.entries(config).filter(([_, v]) => v !== undefined)
            );

            const data = {
                ...sanitizedConfig,
                userId: user.uid,
                updated_at: new Date().toISOString()
            };
            await setDoc(docRef, data, { merge: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userBillingConfig', user?.uid] });
        },
    });
};

// Public Profile & Inventory (for Quotation Form)
export const usePublicProfile = (userId: string | undefined) => {
    return useQuery({
        queryKey: ['publicProfile', userId],
        queryFn: async () => {
            if (!userId) return null;
            const docRef = doc(db, 'user_profiles', userId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return docSnap.data() as Record<string, unknown>;
            }
            return null;
        },
        enabled: !!userId,
    });
};

export const usePublicInventory = (userId: string | undefined) => {
    return useQuery({
        queryKey: ['publicInventory', userId],
        queryFn: async () => {
            if (!userId) return [];
            const q = query(collection(db, 'inventory_packages'), where('userId', '==', userId));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as InventoryPackage));
        },
        enabled: !!userId,
    });
};

export const usePublicArtistEvents = (userId: string | undefined) => {
    return useQuery({
        queryKey: ['publicArtistEvents', userId],
        queryFn: async () => {
            if (!userId) return [];
            const q = query(collection(db, 'events'), where('user_id', '==', userId));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => {
                const data = doc.data();
                return { 
                    date: data.date,
                    status: data.status
                };
            });
        },
        enabled: !!userId,
    });
};

// Events
export const useEvents = (customerId?: string) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['events', customerId, user?.uid],
        queryFn: async () => {
            if (!user) return [];
            let q: ReturnType<typeof query> | ReturnType<typeof collection> = collection(db, 'events');
            if (customerId) {
                q = query(q, where('customer_id', '==', customerId), where('user_id', '==', user.uid));
            } else {
                q = query(q, where('user_id', '==', user.uid));
            }
            const snapshot = await getDocs(q);
            const events = snapshot.docs.map(doc => {
                const data = doc.data() as Record<string, unknown>;
                return { 
                    id: doc.id, 
                    customerId: data.customer_id,
                    title: data.title,
                    date: new Date(data.date),
                    venue: data.venue,
                    cost: data.cost || 0,
                    taxPercentage: data.taxPercentage || 0,
                    taxAmount: data.taxAmount || 0,
                    total: data.total || data.total_with_tax || 0,
                    totalWithTax: data.total_with_tax || data.total || 0,
                    status: data.status,
                    currency: data.currency || 'USD',
                    items: data.items || [],
                    showCosts: data.showCosts || 0,
                    startTime: data.startTime,
                    endTime: data.endTime,
                    comments: data.comments,
                    cabys_code: data.cabys_code,
                    representativeId: data.representativeId,
                    userId: data.user_id,
                    createdAt: new Date(data.created_at || new Date().toISOString()),
                    updatedAt: new Date((data.updated_at as string) || new Date().toISOString())
                } as AppEvent;
            });

            // Sort in memory to avoid needing a composite index
            return events.sort((a, b) => b.date.getTime() - a.date.getTime());
        },
    });
};

export const useEvent = (id: string) => {
    return useQuery({
        queryKey: ['events', id],
        queryFn: async () => {
            const docRef = doc(db, 'events', id);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) throw new Error('Event not found');
            const data = docSnap.data();
            const event = { 
                id: docSnap.id, 
                customerId: data.customer_id,
                title: data.title,
                date: new Date(data.date),
                venue: data.venue,
                cost: data.cost || 0,
                taxPercentage: data.taxPercentage || 0,
                taxAmount: data.taxAmount || 0,
                total: data.total || data.total_with_tax || 0,
                totalWithTax: data.total_with_tax || data.total || 0,
                status: data.status,
                currency: data.currency || 'USD',
                items: data.items || [],
                showCosts: data.showCosts || 0,
                startTime: data.startTime,
                endTime: data.endTime,
                comments: data.comments,
                cabys_code: data.cabys_code,
                representativeId: data.representativeId,
                userId: data.user_id,
                createdAt: new Date(data.created_at || new Date().toISOString()),
                updatedAt: new Date((data.updated_at as string) || new Date().toISOString())
            } as AppEvent;

            const custRef = doc(db, 'customers', event.customerId);
            const custSnap = await getDoc(custRef);
            if (custSnap.exists()) {
                event.customers = { id: custSnap.id, ...custSnap.data() };
            }
            return event;
        },
        enabled: !!id,
    });
};

export const useUpsertEvent = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async (event: AppEvent & { id?: string }) => {
            if (!user) throw new Error('User not authenticated');

            const timestamp = new Date().toISOString();
            const eventData: Record<string, unknown> = { 
                customer_id: event.customerId || event.customer_id || '',
                title: event.title || '',
                date: (event.date instanceof Date ? event.date.toISOString() : event.date) || timestamp,
                venue: event.venue || '',
                cost: event.cost || 0,
                taxPercentage: event.taxPercentage || 0,
                taxAmount: event.taxAmount || 0,
                total: event.total || 0,
                total_with_tax: event.totalWithTax || event.total || 0,
                status: event.status || 'prospect',
                comments: event.comments || '',
                cabys_code: event.cabysCode || event.cabys_code || '',
                representativeId: event.representativeId || null,
                items: event.items || [],
                showCosts: event.showCosts || 0,
                startTime: event.startTime || null,
                endTime: event.endTime || null,
                updated_at: timestamp
            };

            if (event.id) {
                await updateDoc(doc(db, 'events', event.id), eventData);
                return { id: event.id, ...event };
            } else {
                eventData.user_id = user.uid;
                eventData.created_at = timestamp;
                const docRef = await addDoc(collection(db, 'events'), eventData);
                return { id: docRef.id, ...event };
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['events'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
        },
    });
};

export const useUpdateEventStatus = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async ({ id, status }: { id: string, status: string }) => {
            if (!user) throw new Error('User not authenticated');
            const timestamp = new Date().toISOString();
            await updateDoc(doc(db, 'events', id), { 
                status, 
                updated_at: timestamp 
            });
            return { id, status };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['events'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
        },
    });
};

export const useDeleteEvent = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            await deleteDoc(doc(db, 'events', id));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['events'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
        },
    });
};

// Payments
export const usePayments = (eventId?: string) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['payments', eventId, user?.uid],
        queryFn: async () => {
            if (!user) return [];
            let q = query(collection(db, 'payments'), where('user_id', '==', user.uid));
            if (eventId) {
                q = query(q, where('event_id', '==', eventId));
            }
            const snapshot = await getDocs(q);
            const payments = snapshot.docs.map(doc => {
                const data = doc.data() as Record<string, unknown>;
                return { 
                    id: doc.id, 
                    eventId: data.event_id,
                    amount: data.amount,
                    currency: data.currency,
                    paymentDate: new Date(data.payment_date),
                    method: data.method,
                    notes: data.notes,
                    createdAt: new Date(data.created_at || new Date().toISOString()),
                    updatedAt: new Date((data.updated_at as string) || new Date().toISOString())
                } as Payment;
            });

            // Sort in memory
            return payments.sort((a, b) => b.paymentDate.getTime() - a.paymentDate.getTime());
        },
    });
};

export const useUpsertPayment = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async (payment: PaymentInsert & { id?: string }) => {
            const timestamp = new Date().toISOString();
            const paymentData: Record<string, unknown> = { 
                event_id: payment.eventId,
                amount: payment.amount,
                currency: payment.currency,
                payment_date: payment.paymentDate instanceof Date ? payment.paymentDate.toISOString() : payment.paymentDate,
                method: payment.method,
                notes: payment.notes || '',
                user_id: user?.uid || null,
                updated_at: timestamp
            };

            if (payment.id) {
                await updateDoc(doc(db, 'payments', payment.id), paymentData);
                return { id: payment.id, ...payment };
            } else {
                paymentData.created_at = timestamp;
                const docRef = await addDoc(collection(db, 'payments'), paymentData);
                return { id: docRef.id, ...payment };
            }
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            if (variables.eventId) {
                queryClient.invalidateQueries({ queryKey: ['payments', variables.eventId] });
                queryClient.invalidateQueries({ queryKey: ['events', variables.eventId] });
            }
        },
    });
};

export const useDeletePayment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, eventId }: { id: string; eventId?: string }) => {
            await deleteDoc(doc(db, 'payments', id));
            return { id, eventId };
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            if (data.eventId) {
                queryClient.invalidateQueries({ queryKey: ['payments', data.eventId] });
                queryClient.invalidateQueries({ queryKey: ['events', data.eventId] });
            }
        },
    });
};

// Proposals
export const useProposals = () => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['proposals', user?.uid],
        queryFn: async () => {
            if (!user) return [];
            const q = query(
                collection(db, 'proposals'), 
                where('artistId', '==', user.uid),
                orderBy('created_at', 'desc') // requires a composite index in firestore if filtering+ordering
            );
            // Firebase might require an index for where() + orderBy(). If error occurs, we handle it or just sort locally.
            // Let's query by artistId and sort in memory to avoid strict composite index requirements from a fresh setup.
            const qSimple = query(collection(db, 'proposals'), where('artistId', '==', user.uid));
            const snapshot = await getDocs(qSimple);
            const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Proposal));
            return docs.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        },
        enabled: !!user,
    });
};

export const useCreateProposal = () => {
    return useMutation({
        mutationFn: async (proposal: ProposalInsert & { artistId: string }) => {
            const timestamp = new Date().toISOString();
            const proposalData: Record<string, unknown> = { ...proposal, created_at: timestamp };
            const docRef = await addDoc(collection(db, 'proposals'), proposalData);
            
            // Add notification for the artist
            try {
                await addDoc(collection(db, 'notifications'), {
                    userId: proposal.artistId,
                    type: 'quotation',
                    title: 'Nueva solicitud de cotización',
                    message: `Has recibido una nueva solicitud de ${proposal.name} para un evento.`,
                    targetId: docRef.id,
                    targetType: 'quotation',
                    isRead: false,
                    createdAt: serverTimestamp(),
                });
            } catch (error) {
                console.error('Error creating notification:', error);
            }

            return { id: docRef.id, ...proposalData };
        },
    });
};

// --- Inventory Packages ---

export const useInventoryPackages = () => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['inventory-packages', user?.uid],
        queryFn: async () => {
            if (!user) return [];
            const q = query(collection(db, 'inventory_packages'), where('userId', '==', user.uid));
            const snapshot = await getDocs(q);
            const packages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as InventoryPackage));
            // Sort client-side by name string
            return packages.sort((a, b) => a.name.localeCompare(b.name));
        },
        enabled: !!user,
    });
};

export const useCreateInventoryPackage = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (pkg: InventoryPackageInsert) => {
            if (!user) throw new Error('Not authenticated');
            
            // Firestore doesn't accept undefined values, so we must clean the object
            const cleanPkg = Object.fromEntries(
                Object.entries(pkg).filter(([_, v]) => v !== undefined)
            );
            
            const newPkg = { ...cleanPkg, createdAt: new Date().toISOString(), userId: user.uid };
            const docRef = await addDoc(collection(db, 'inventory_packages'), newPkg);
            return { id: docRef.id, ...newPkg };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inventory-packages'] });
        },
    });
};

export const useUpdateInventoryPackage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, ...updates }: Partial<InventoryPackage> & { id: string }) => {
            const packageRef = doc(db, 'inventory_packages', id);
            
            // Clean undefined values
            const cleanUpdates = Object.fromEntries(
                Object.entries(updates).filter(([_, v]) => v !== undefined)
            );
            
            await updateDoc(packageRef, cleanUpdates);
            return { id, ...updates };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inventory-packages'] });
        },
    });
};

export const useDeleteInventoryPackage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const packageRef = doc(db, 'inventory_packages', id);
            await deleteDoc(packageRef);
            return id;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inventory-packages'] });
        },
    });
};

// Dashboard stats with multi-currency support
export const useDashboardStats = () => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['dashboard-stats', user?.uid],
        queryFn: async () => {
            if (!user) {
                return {
                    totalCustomers: 0,
                    totalEvents: 0,
                    totalsByCurrency: {},
                    paidByCurrency: {},
                    events: [],
                    payments: [],
                    hasMultipleCurrencies: false,
                    salesOverview: [],
                    profitOverview: null,
                    leadsOverview: [],
                    salesPerformance: [],
                };
            }

            // 1. Parallelize fetching Events, Customers, and Proposals
            const [eventsSnap, customersSnap, proposalsSnap] = await Promise.all([
                getDocs(query(collection(db, 'events'), where('user_id', '==', user.uid))),
                getDocs(query(collection(db, 'customers'), where('user_id', '==', user.uid))),
                getDocs(query(collection(db, 'proposals'), where('artistId', '==', user.uid)))
            ]);

            const events = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() } as AppEvent));
            events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
            
            const customers = customersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const totalCustomers = customersSnap.size;
            const totalProposals = proposalsSnap.size;

            // 2. Fetch Payments efficiently 
            const userEventIds = new Set(events.map(e => e.id));
            const payments: Payment[] = [];
            
            // Query all payments for this user
            const paymentsSnap = await getDocs(query(collection(db, 'payments'), where('user_id', '==', user.uid)));
            
            paymentsSnap.forEach(docSnap => {
                const data = docSnap.data();
                // Only include payments that belong to the user's events
                if (userEventIds.has(data.event_id)) {
                    payments.push({ id: docSnap.id, ...data } as Payment);
                }
            });

            // 3. Group totals by status and currency
            let totalOverallSales = 0; // for ProfitOverview (using base currency or just sum for demo)
            let totalOverallRevenue = 0;
            let totalOverallExpenses = 0;
            
            const stats = events.reduce((acc, event) => {
                const currency = event.currency || 'USD';
                const status = event.status || 'prospect';
                const total = parseFloat(event.total?.toString() || '0');
                const expenses = parseFloat(event.expenses?.toString() || '0');

                if (!acc.totalsByCurrency[currency]) acc.totalsByCurrency[currency] = 0;
                if (!acc.confirmedTotalsByCurrency[currency]) acc.confirmedTotalsByCurrency[currency] = 0;
                if (!acc.potentialTotalsByCurrency[currency]) acc.potentialTotalsByCurrency[currency] = 0;

                acc.totalsByCurrency[currency] += total;
                totalOverallSales += total; // simplified unified total
                totalOverallExpenses += expenses;

                const isConfirmed = ['confirmed', 'show_completed', 'paid'].includes(status);
                const isProspect = status === 'prospect';

                if (isConfirmed) {
                    acc.totalConfirmedShows++;
                    acc.confirmedTotalsByCurrency[currency] += total;
                } else if (isProspect) {
                    acc.totalProspects++;
                    acc.potentialTotalsByCurrency[currency] += total;
                }

                return acc;
            }, {
                totalConfirmedShows: 0,
                totalProspects: 0,
                totalsByCurrency: {} as Record<string, number>,
                confirmedTotalsByCurrency: {} as Record<string, number>,
                potentialTotalsByCurrency: {} as Record<string, number>
            });

            // 4. Group payments by currency
            const paidByCurrency = payments.reduce((acc, payment) => {
                const currency = payment.currency || 'USD';
                if (!acc[currency]) {
                    acc[currency] = 0;
                }
                const amount = parseFloat(payment.amount?.toString() || '0');
                acc[currency] += amount;
                totalOverallRevenue += amount;
                return acc;
            }, {} as Record<string, number>);

            // 5. Calculate receivable (Confirmed - Paid)
            const receivableByCurrency = { ...stats.confirmedTotalsByCurrency };
            Object.keys(paidByCurrency).forEach(currency => {
                if (!receivableByCurrency[currency]) receivableByCurrency[currency] = 0;
                receivableByCurrency[currency] -= paidByCurrency[currency];
                if (receivableByCurrency[currency] < 0) receivableByCurrency[currency] = 0;
            });

            // --- NEW: Calculate Data for Dashboard Charts ---

            // A) Sales Overview Chart (Monthly breakdown of Income, Revenue, Profit)
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const salesOverviewMap: Record<string, { name: string; income: number; revenue: number; profit: number }> = {};
            
            // Initialize 12 months
            monthNames.forEach(m => salesOverviewMap[m] = { name: m, income: 0, revenue: 0, profit: 0 });

            events.forEach(event => {
                const date = new Date(event.date);
                if (date.getFullYear() === new Date().getFullYear()) {
                    const month = monthNames[date.getMonth()];
                    const total = parseFloat(event.total?.toString() || '0');
                    // Income is all potentials, Revenue is paid, Profit is an estimate (e.g., 30% of total)
                    salesOverviewMap[month].income += total;
                    salesOverviewMap[month].profit += total * 0.3; // estimated profit for now
                }
            });

            payments.forEach(payment => {
                const date = new Date(payment.paymentDate || payment.payment_date);
                if (date.getFullYear() === new Date().getFullYear()) {
                    const month = monthNames[date.getMonth()];
                    const amount = parseFloat(payment.amount?.toString() || '0');
                    salesOverviewMap[month].revenue += amount;
                }
            });

            const salesOverview = monthNames.map(m => salesOverviewMap[m]);

            // B) Profit By Sale Overview
            const totalProfit = totalOverallSales - totalOverallExpenses;
            const profitOverview = {
                totalSales: totalOverallSales,
                totalSalesGrowth: 15, // Example
                totalProfit: totalProfit,
                totalProfitGrowth: 8,
                totalRevenue: totalOverallRevenue,
                totalRevenueGrowth: 12,
                totalLoss: totalOverallExpenses,
                totalLossGrowth: -5
            };

            // C) Leads Overview (Radar Chart)
            const leadTypes = ['Prospecto', 'Recurrente', 'Esporádico', 'Eventos masivos', 'Corporativo'];
            const leadsCount: Record<string, number> = {};
            leadTypes.forEach(t => leadsCount[t] = 0);
            
            customers.forEach(customer => {
                const cType = customer.clientType || 'Prospecto';
                if (leadsCount[cType] !== undefined) {
                    leadsCount[cType]++;
                } else {
                    leadsCount['Prospecto']++; // Fallback
                }
            });
            const leadsOverview = leadTypes.map(type => ({ type, count: leadsCount[type] }));

            // D) Sales Performance (By Representative)
            const repsMap: Record<string, { name: string, dealsClosed: number, leads: number }> = {};
            events.forEach(event => {
                const repId = event.representativeId || 'You (Me)';
                if (!repsMap[repId]) {
                    repsMap[repId] = { name: repId, dealsClosed: 0, leads: 0 };
                }
                repsMap[repId].leads++;
                if (['confirmed', 'show_completed', 'paid'].includes(event.status)) {
                    repsMap[repId].dealsClosed++;
                }
            });

            const salesPerformance = Object.keys(repsMap).map(id => {
                const r = repsMap[id];
                const rate = r.leads > 0 ? (r.dealsClosed / r.leads) * 100 : 0;
                return {
                    id,
                    name: r.name,
                    dealsClosed: r.dealsClosed,
                    leads: r.leads,
                    rate,
                    isPositive: rate > 15
                };
            }).sort((a, b) => b.dealsClosed - a.dealsClosed);

            // E) Financial KPIs
            let confirmedEventCount = 0;
            let confirmedSalesTotal = 0;
            
            events.forEach(evt => {
               if (evt.status !== 'prospect' && evt.status !== 'cancelled') {
                   confirmedEventCount++;
                   confirmedSalesTotal += parseFloat(evt.total?.toString() || '0');
               }
            });
            
            const accountsReceivable = Object.values(receivableByCurrency).reduce((a, b) => a + b, 0);
            const avgTicket = confirmedEventCount > 0 ? confirmedSalesTotal / confirmedEventCount : 0;
            const avgCost = confirmedEventCount > 0 ? totalOverallExpenses / confirmedEventCount : 0;
            const profitMargin = confirmedSalesTotal > 0 ? ((confirmedSalesTotal - totalOverallExpenses) / confirmedSalesTotal) * 100 : 0;

            const financialKpis = {
                totalRevenue: totalOverallRevenue,
                accountsReceivable,
                avgTicket,
                profitMargin,
                avgCost
            };

            return {
                totalCustomers,
                totalEvents: events.length,
                totalConfirmedShows: stats.totalConfirmedShows,
                totalProspects: stats.totalProspects,
                totalProposals,
                totalsByCurrency: stats.totalsByCurrency,
                confirmedTotalsByCurrency: stats.confirmedTotalsByCurrency,
                potentialTotalsByCurrency: stats.potentialTotalsByCurrency,
                receivableByCurrency,
                paidByCurrency,
                events,
                hasMultipleCurrencies: Object.keys(stats.totalsByCurrency).length > 1,
                salesOverview,
                profitOverview,
                leadsOverview,
                salesPerformance,
                financialKpis,
                payments
            };
        },
    });
};
